import { useEffect, useMemo } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import { receiveArrivalSchedule } from '#/api/srm/purchaseOrder/arrivalSchedule/receive';
import { fetchPurchaseOrderArrivalScheduleDetail } from '#/api/srm/purchaseOrder/fetchArrivalScheduleDetail';
import { fetchPurchaseOrderDetail } from '#/api/srm/purchaseOrder/fetchDetail';
import { DocumentStatusBadge } from '#/modules/commonComponents/badge/DocumentStatusBadge';
import { Button } from '#/modules/commonComponents/button';
import FloatingDateInput from '#/modules/commonComponents/form/FloatingDateInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import StockReceiptHeaderChargeFields from
  '#/modules/imsMgmt/receipts/stockReceipt/components/StockReceiptHeaderChargeFields';
import FloatingSelect from '#/modules/commonComponents/form/select';
import FormValidationSummary from '#/modules/commonComponents/form/FormValidationSummary';
import useFormValidationSummary from '#/hooks/useFormValidationSummary';
import PageShell from '#/modules/infra/layout/PageShell';
import StockReceiptLinesEditor from '#/modules/imsMgmt/receipts/stockReceipt/components/StockReceiptLinesEditor';
import { STOCK_RECEIPT_RECEIPT_DATE_TIP } from '#/modules/imsMgmt/receipts/stockReceipt/helper/receiptDateTip';
import {
  resolveStockReceiptFieldLabel,
} from '#/modules/imsMgmt/receipts/stockReceipt/helper/formValidationLabels';
import { STOCK_RECEIPT_FIELD_LABELS } from '#/modules/imsMgmt/receipts/stockReceipt/schemas/stockReceiptSchema';
import { fetchWarehouseSelectOptions } from '#/modules/imsMgmt/settings/storePlace/helper/warehouseSelectOptions';
import {
  getPurchaseOrderApiErrorMessage,
} from '#/modules/srmMgmt/purchaseOrder/helper/apiError';
import {
  canReceiveArrivalSchedule,
} from '#/modules/srmMgmt/purchaseOrder/helper/arrivalScheduleStatus';
import {
  buildArrivalScheduleReceiveFormSchema,
  buildArrivalScheduleStockReceiptDefaultValues,
  sumReceiptFormQty,
} from '#/modules/srmMgmt/purchaseOrder/helper/mapArrivalScheduleToStockReceiptForm';
import {
  buildPurchaseOrderStockReceiptPayload,
} from '#/modules/srmMgmt/purchaseOrder/helper/mapPurchaseOrderToStockReceiptForm';
import { toast } from '#/utils/toast';
import { formatDisplayQty } from '#/utils/formatNumber';
import {
  lookupQueryKey,
  purchaseOrderArrivalScheduleDetailQueryKey,
  purchaseOrderArrivalScheduleListQueryKeyPrefix,
  purchaseOrderDetailQueryKey,
  purchaseOrderStockReceiptListQueryKeyPrefix,
  stockReceiptListQueryKeyPrefix,
} from '#/utils/queryKeys';

const CreateArrivalScheduleStockReceiptPage = () => {
  const { id: purchaseOrderId, scheduleId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    isSubmitAttempted,
    markSubmitAttempted,
    resetValidationSummary,
    customMessages,
  } = useFormValidationSummary();

  const poQuery = useQuery({
    queryKey: purchaseOrderDetailQueryKey(purchaseOrderId),
    queryFn: () => fetchPurchaseOrderDetail(purchaseOrderId),
    enabled: Boolean(purchaseOrderId),
  });

  const scheduleQuery = useQuery({
    queryKey: purchaseOrderArrivalScheduleDetailQueryKey(
      purchaseOrderId,
      scheduleId,
    ),
    queryFn: () => fetchPurchaseOrderArrivalScheduleDetail(
      purchaseOrderId,
      scheduleId,
    ),
    enabled: Boolean(purchaseOrderId && scheduleId),
  });

  const warehouseOptionsQuery = useQuery({
    queryKey: lookupQueryKey('warehouseSelect'),
    queryFn: fetchWarehouseSelectOptions,
  });

  const poDetail = poQuery.data?.data;
  const schedule = scheduleQuery.data?.data;
  const remainingQtyMax = schedule?.remainingQty;
  const canReceive = canReceiveArrivalSchedule(schedule);
  const warehouseOptions = warehouseOptionsQuery.data ?? [];

  const formSchema = useMemo(
    () => buildArrivalScheduleReceiveFormSchema(remainingQtyMax),
    [remainingQtyMax],
  );

  const defaultValues = useMemo(
    () => buildArrivalScheduleStockReceiptDefaultValues(schedule, poDetail),
    [schedule, poDetail],
  );

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: buildArrivalScheduleStockReceiptDefaultValues(null, null),
  });

  useEffect(() => {
    if (!schedule || !poDetail) {
      return;
    }
    resetValidationSummary();
    reset(defaultValues);
  }, [schedule, poDetail, defaultValues, reset, resetValidationSummary]);

  const createMutation = useMutation({
    mutationFn: (values) => receiveArrivalSchedule(
      purchaseOrderId,
      scheduleId,
      buildPurchaseOrderStockReceiptPayload(values),
    ),
    onSuccess: async (result) => {
      toast.success(result?.message || '入庫單已建立');
      await queryClient.invalidateQueries({
        queryKey: purchaseOrderDetailQueryKey(purchaseOrderId),
      });
      await queryClient.invalidateQueries({
        queryKey: purchaseOrderStockReceiptListQueryKeyPrefix(purchaseOrderId),
      });
      await queryClient.invalidateQueries({
        queryKey: stockReceiptListQueryKeyPrefix(),
      });
      await queryClient.invalidateQueries({
        queryKey: purchaseOrderArrivalScheduleListQueryKeyPrefix(purchaseOrderId),
      });
      await queryClient.invalidateQueries({
        queryKey: purchaseOrderArrivalScheduleDetailQueryKey(
          purchaseOrderId,
          scheduleId,
        ),
      });
      navigate(ROUTES.purchaseOrderDetail(purchaseOrderId));
    },
    onError: (error) => {
      toast.error(getPurchaseOrderApiErrorMessage(error, '排程建入庫失敗'));
    },
  });

  function handleInvalidSubmit() {
    markSubmitAttempted();
  }

  function onSubmit(values) {
    markSubmitAttempted();
    if (!canReceive) {
      toast.error('此排程不可建入庫（需已確認／部分入庫且尚未有進貨單）');
      return;
    }
    const max = Number(remainingQtyMax);
    if (!Number.isNaN(max) && sumReceiptFormQty(values) > max) {
      toast.error(`合計數量不可超過排程剩餘量 ${max}`);
      return;
    }
    createMutation.mutate(values);
  }

  const isLoading = poQuery.isLoading || scheduleQuery.isLoading;
  const isError = poQuery.isError || scheduleQuery.isError;
  const loadError = poQuery.error || scheduleQuery.error;
  const ready = Boolean(poDetail && schedule);

  if (!isLoading && ready && !canReceive) {
    return <Navigate to={ROUTES.purchaseOrderDetail(purchaseOrderId)} replace />;
  }

  return (
    <PageShell variant="form">
      <div className="layout-shell__body">
        <div className="w-full">
          <div className="create_edit_page__header">
            <h1 className="page_title mb-0! flex flex-wrap items-center gap-2">
              <span className="border-l-5 border-primary-400 pl-2.5">
                排程建入庫
              </span>
              <DocumentStatusBadge status="DRAFT" size="sm" />
            </h1>
            <Button type="button" variant="outline" asChild>
              <Link to={ROUTES.purchaseOrderDetail(purchaseOrderId)}>返回詳情</Link>
            </Button>
          </div>

          {isLoading ? (
            <p className="text-sm text-slate-500">載入中…</p>
          ) : null}

          {isError ? (
            <p className="text-sm text-destructive">
              {getPurchaseOrderApiErrorMessage(loadError, '載入失敗')}
            </p>
          ) : null}

          {!isLoading && !isError && ready ? (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                物料採購單
                {' '}
                {poDetail.purchaseOrderNo || purchaseOrderId}
                {' · '}
                排程剩餘量
                {' '}
                {formatDisplayQty(remainingQtyMax)}
                （合計 qty 不可超過此值；一排程一張進貨單）
              </p>

              <FormValidationSummary
                show={isSubmitAttempted}
                errors={errors}
                fieldLabels={STOCK_RECEIPT_FIELD_LABELS}
                fieldLabelResolver={(path) => resolveStockReceiptFieldLabel(path, {
                  lines: getValues('lines'),
                })}
                customMessages={customMessages}
              />

              <form
                className="sheet create_edit create_edit_form create_edit_form--page-scroll"
                onSubmit={handleSubmit(onSubmit, handleInvalidSubmit)}
                noValidate
              >
                <div className="create_edit_form__body divide-y divide-gray-500">
                  <section className="pb-6">
                    <div className="form-block">
                      <h2 className="form-block__title">單據資訊</h2>
                    </div>
                    <div
                      className={cn(
                        'grid grid-cols-1 gap-3',
                        '@4xl/sheet:grid-cols-2',
                      )}
                    >
                      <FloatingDateInput
                        id="receiptDate"
                        label="製造商開單日"
                        tip={STOCK_RECEIPT_RECEIPT_DATE_TIP}
                        control={control}
                        errors={errors}
                        placeholder="西元年/月/日"
                      />
                      <FloatingDateInput
                        id="expectedArrivalDate"
                        label="預計到貨日"
                        control={control}
                        errors={errors}
                        placeholder="西元年/月/日"
                      />
                      <FloatingSelect
                        id="warehouseId"
                        label="倉別"
                        options={warehouseOptions}
                        control={control}
                        errors={errors}
                        isDisabled={warehouseOptionsQuery.isLoading}
                        noOptionsMessage="無可用倉別"
                      />
                      <FloatingTextarea
                        id="issueReason"
                        label="事由"
                        register={register}
                        errors={errors}
                        rows={2}
                      />
                      <FloatingTextarea
                        id="remark"
                        label="備註"
                        register={register}
                        errors={errors}
                        rows={2}
                      />
                      <StockReceiptHeaderChargeFields
                        register={register}
                        errors={errors}
                      />
                    </div>
                  </section>

                  <StockReceiptLinesEditor
                    control={control}
                    register={register}
                    errors={errors}
                    setValue={setValue}
                  />
                </div>

                <div className="create_edit_form__actions mt-4 flex flex-wrap justify-end gap-2">
                  <Button type="button" variant="outline" asChild>
                    <Link to={ROUTES.purchaseOrderDetail(purchaseOrderId)}>取消</Link>
                  </Button>
                  <Button
                    type="submit"
                    disabled={createMutation.isPending || !canReceive}
                  >
                    {createMutation.isPending ? '建立中…' : '建立入庫單'}
                  </Button>
                </div>
              </form>
            </div>
          ) : null}
        </div>
      </div>
    </PageShell>
  );
};

export default CreateArrivalScheduleStockReceiptPage;
